'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import { RadioGroup as RACRadioGroup, type RadioGroupProps as RACRadioGroupProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { Stack } from '../spacing/stack/stack.js';
import type { Breakpoint, Responsive, Spacing } from '../spacing/types.js';
import type { Direction, CommonProps, FieldProps } from '../types.js';

export interface RadioGroupProps
  extends
    CommonProps,
    FieldProps<string | null>,
    Pick<
      RACRadioGroupProps,
      'isDisabled' | 'isReadOnly' | 'isRequired' | 'isInvalid' | 'value' | 'onChange' | 'defaultValue'
    > {
  /** Radio buttons belonging to the group. */
  children: ReactNode;
  /**
   * Determines the direction in which radio buttons will be stacked.
   * @default 'vertical'
   */
  direction?: Responsive<Direction>;
}

function RadioGroup(
  {
    label,
    description,
    error: errorMessage,
    direction = 'vertical',
    children,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: RadioGroupProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('RadioGroup requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  return (
    <RACRadioGroup {...props} ref={ref} className={clsx('cim-radio-group', UNSAFE_className)} style={UNSAFE_style}>
      <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
        {label}
      </FormFieldLabel>
      <Stack gap={getGapFromDirection(direction)} direction={direction} wrap>
        {children}
      </Stack>
      <FormFieldError>{errorMessage}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>
    </RACRadioGroup>
  );
}

/**
 * Allows users to select a single item from a visible list.
 *
 * See [radio group usage guidelines](https://ui.cimpress.io/components/radio-group/).
 */
const _RadioGroup = withStyleProps(forwardRef(RadioGroup), 'RadioGroup');

export { _RadioGroup as RadioGroup };

// Make sure that radio buttons are spaced by 16px when stacked horizontally, and 8px otherwise.
function getGapFromDirection(direction: Responsive<Direction>): Responsive<Spacing> {
  if (typeof direction === 'object') {
    const gap: Responsive<Spacing> = { sm: direction.sm === 'horizontal' ? 16 : 8 };

    (Object.entries(direction) as [Breakpoint, Direction][]).reduce((acc, curr) => {
      gap[curr[0]] = curr[1] === 'horizontal' ? 16 : 8;
      return acc;
    }, gap);

    return gap;
  }

  return direction === 'horizontal' ? 16 : 8;
}
